import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown, Check, AlertCircle, Pin } from "lucide-react";
import api from "~/services/api";
import { FullGridContext } from "../..";

const STORAGE_KEY = "prearrivals_pinned_filters";
const STORAGE_PINNED_KEY = "prearrivals_filters_pinned";

const EMPTY_FILTERS = {
  status: "",
  process: "",
  substatus: "",
  agent: "",
  programStartDateFrom: "",
  programStartDateTo: "",
  expectedStartDateFrom: "",
  expectedStartDateTo: "",
  interviewDateFrom: "",
  interviewDateTo: "",
};

export function getStoredFilters() {
  try {
    const pinned = localStorage.getItem(STORAGE_PINNED_KEY) === "true";
    if (!pinned) return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const Toast = ({ message, show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-5 duration-300">
      <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl flex items-center gap-3">
        <AlertCircle className="w-5 h-5" />
        <span className="font-medium text-sm">{message}</span>
        <button onClick={onClose} className="ml-2 hover:bg-red-700 p-1 rounded-full">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState(
    options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      ""
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const next =
      options.find((opt) => opt === value)?.toString() ||
      options.find((opt) => opt.value === value)?.label ||
      options.find((opt) => opt.label === value)?.label ||
      value ||
      "";
    setQuery(next);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

  const filteredOptions = normalizedOptions.filter((opt) =>
    String(opt.label ?? "").toLowerCase().includes(String(query ?? "").toLowerCase())
  );

  const handleSelect = (option) => {
    setQuery(option.label);
    onChange(option.value);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
        {label}
      </label>

      <div className="relative">
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-xl max-h-48 rounded-md py-1 text-sm overflow-auto border border-gray-200 top-full left-0">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500 italic">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-4 py-2 hover:bg-orange-50 flex items-center justify-between ${
                    option.value === value
                      ? "bg-orange-50 text-orange-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {option.label}
                  {option.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FILTER_FIELDS = [
  { key: "status", label: "Status" },
  { key: "process", label: "Process" },
  { key: "substatus", label: "Process Substatus" },
  { key: "agent", label: "Responsible Agent" },
];

export default function ModalFilters({
  opened = false,
  onClose = () => null,
  applying = false,
  onApplyFilters = () => null,
}) {
  const { orderBy, search } = useContext(FullGridContext);
  const [formData, setFormData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...EMPTY_FILTERS, ...JSON.parse(raw) } : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  const [pinned, setPinned] = useState(
    () => localStorage.getItem(STORAGE_PINNED_KEY) === "true"
  );

  const [toastState, setToastState] = useState({ show: false, message: "" });
  const [isClosing, setIsClosing] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    statuses: [],
    processes: [],
    subStatuses: [],
    agents: [],
  });

  useEffect(() => {
    if (!opened) return;

    let isMounted = true;
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const { data } = await api.get("/pre-arrivals/filters-get");
        if (!isMounted) return;
        const optionGroup = (value) => (Array.isArray(value) ? value : []);
        setFilterOptions({
          statuses: optionGroup(data?.statuses || data?.status),
          processes: optionGroup(data?.types || data?.processTypes),
          subStatuses: optionGroup(data?.subStatuses || data?.processSubstatuses),
          agents: optionGroup(data?.agents),
        });
      } catch (err) {
        if (!isMounted) return;
        setToastState({
          show: true,
          message: "Failed to load filter options",
        });
      } finally {
        if (isMounted) {
          setLoadingOptions(false);
        }
      }
    };

    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, [opened]);

  const optionsByKey = useMemo(() => {
    const mapToOptions = (values) =>
      (values || []).map((option) => {
        if (typeof option === "boolean") {
          return { value: option, label: option ? "Yes" : "No" };
        }
        if (option && typeof option === "object") {
          const value =
            option.value ?? option.id ?? option.name ?? option.label ?? option;
          const label =
            option.label ?? option.name ?? option.value ?? option.id ?? "";
          return { value, label: label?.toString?.() || "" };
        }
        const label = option?.toString?.() || "";
        return { value: option, label };
      });

    return {
      status: mapToOptions(filterOptions.statuses),
      process: mapToOptions(filterOptions.processes),
      substatus: mapToOptions(filterOptions.subStatuses),
      agent: mapToOptions(filterOptions.agents),
    };
  }, [filterOptions]);

  const buttonBaseClasses =
    "px-4 py-2 text-sm font-medium border rounded-lg transition-colors shadow-xs flex-1 justify-center items-center flex";
  const secondaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const primaryButtonClasses = `${buttonBaseClasses} text-gray-700 bg-white border-gray-300 hover:bg-gray-50`;
  const clearButtonClasses =
    "w-full py-2 text-sm font-medium text-red-600 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors mb-1";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(STORAGE_PINNED_KEY, String(next));
    if (!next) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleApplyFilters = () => {
    const invalidField = FILTER_FIELDS.find((field) => {
      const value = formData[field.key];
      if (!value) return false;
      const options = optionsByKey[field.key] || [];
      return !options.some((opt) => opt.value === value);
    });

    if (invalidField) {
      setToastState({
        show: true,
        message: `Option Invalid, choose a option for ${invalidField.label}`,
      });
      return;
    }

    const params = new URLSearchParams();
    const orderColumn = orderBy?.column || "name";
    const orderDirection = orderBy?.asc ? "ASC" : "DESC";

    params.set("limit", "50");
    params.set("page", "1");
    params.set("orderBy", orderColumn);
    params.set("orderASC", orderDirection);
    params.set("search", search?.value || "");
    params.set("status", formData.status || "null");
    params.set("type", formData.process || "null");
    params.set("substatus", formData.substatus || "null");
    params.set("agent", formData.agent || "null");
    if (formData.programStartDateFrom) {
      params.set("program_start_date_from", formData.programStartDateFrom);
    }
    if (formData.programStartDateTo) {
      params.set("program_start_date_to", formData.programStartDateTo);
    }
    if (formData.expectedStartDateFrom) {
      params.set(
        "student_expected_start_date_from",
        formData.expectedStartDateFrom
      );
    }
    if (formData.expectedStartDateTo) {
      params.set(
        "student_expected_start_date_to",
        formData.expectedStartDateTo
      );
    }
    if (formData.interviewDateFrom) {
      params.set("interview_date_from", formData.interviewDateFrom);
    }
    if (formData.interviewDateTo) {
      params.set("interview_date_to", formData.interviewDateTo);
    }

    if (pinned) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }

    const queryString = `/pre-arrivals?${params.toString()}`;
    onApplyFilters(queryString);

    handleClose();
  };

  if (!opened) return null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideOutRight {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }

        .modal-slide-in {
          animation: slideInRight 0.3s ease-out forwards;
        }

        .modal-slide-out {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>

      <Toast
        show={toastState.show}
        message={toastState.message}
        onClose={() => setToastState({ ...toastState, show: false })}
      />

      <div
        className="fixed inset-0 z-50 flex justify-end items-center py-6"
        style={{ backgroundColor: "rgba(0,0,0,.75)" }}
        onClick={(event) => {
          if (event.target === event.currentTarget) handleClose();
        }}
      >
        <div
          className={`bg-white w-full max-w-md h-full flex flex-col shadow-2xl overflow-hidden rounded-l-2xl ${
            isClosing ? "modal-slide-out" : "modal-slide-in"
          }`}
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-white shrink-0">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-tight">
                Filters
              </h2>
              <p className="text-xs text-gray-500 font-medium">
                Refine your search results
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTogglePin}
                title={pinned ? "Unpin filters" : "Pin filters"}
                className={`p-2 rounded-md transition-colors ${pinned ? "bg-orange-100 text-orange-600 hover:bg-orange-200" : "hover:bg-gray-100 text-gray-400 hover:text-gray-600"}`}
              >
                <Pin size={18} className={pinned ? "fill-orange-500" : ""} />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
                aria-label="Close filters"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-gray-700 custom-scrollbar">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-5">
                {FILTER_FIELDS.map((field) => (
                  <SearchableSelect
                    key={field.key}
                    label={field.label}
                    options={optionsByKey[field.key] || []}
                    value={formData[field.key]}
                    onChange={(val) => handleChange(field.key, val)}
                    placeholder={loadingOptions ? "Loading options..." : "Select an option"}
                  />
                ))}
                <div className="pt-6 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-5 tracking-wider">
                    Program Start Date Range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Program Start Date From
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.programStartDateFrom}
                        onChange={(e) => handleChange("programStartDateFrom", e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Program Start Date To
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.programStartDateTo}
                        onChange={(e) => handleChange("programStartDateTo", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-5 tracking-wider">
                    Expected Start Date Range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Expected Start Date From
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.expectedStartDateFrom}
                        onChange={(e) =>
                          handleChange("expectedStartDateFrom", e.target.value)
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Expected Start Date To
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.expectedStartDateTo}
                        onChange={(e) =>
                          handleChange("expectedStartDateTo", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-200">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-5 tracking-wider">
                    Interview Date Range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Interview Date From
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.interviewDateFrom}
                        onChange={(e) => handleChange("interviewDateFrom", e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase">
                        Interview Date To
                      </label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={formData.interviewDateTo}
                        onChange={(e) => handleChange("interviewDateTo", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 shrink-0 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => {
                setFormData(EMPTY_FILTERS);
                localStorage.removeItem(STORAGE_KEY);
              }}
              className={clearButtonClasses}
              disabled={applying}
            >
              Clean All Filters
            </button>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={handleClose}
                className={secondaryButtonClasses}
                disabled={applying}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className={primaryButtonClasses}
                disabled={applying}
              >
                {applying ? "Applying..." : "Apply Filters"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
